import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Quote as QuoteIcon, Sparkles, X, BookOpen } from 'lucide-react';
import { useFirstVisitStore } from '@/state/firstVisitStore';
import { getGuideForPath, guideKey, type ScreenGuide } from '@/data/screenGuides';

/**
 * Renders the centralised route-guide modal. Opens only on a manual "force"
 * trigger from the Help button (see HelpLauncher) — showing the current
 * route's guide on demand. Auto-open on first visit is intentionally disabled.
 */
export default function FirstVisitModal() {
  const location = useLocation();
  const forcedKey = useFirstVisitStore((s) => s.forcedKey);
  const markSeen = useFirstVisitStore((s) => s.markSeen);
  const muteAll = useFirstVisitStore((s) => s.muteAll);
  const force = useFirstVisitStore((s) => s.force);

  const guide = useMemo<ScreenGuide | undefined>(() => {
    // If forced, look up that specific guide. Otherwise match the current path.
    if (forcedKey) {
      return getGuideForPath(forcedKey) ?? getGuideForPath(location.pathname);
    }
    return getGuideForPath(location.pathname);
  }, [forcedKey, location.pathname]);

  const open = useMemo(() => {
    // Auto-open on first visit is disabled — the modal only opens when the
    // Help button forces it (see HelpLauncher / firstVisitStore.force).
    if (!guide) return false;
    return Boolean(forcedKey);
  }, [guide, forcedKey]);

  // Close on Esc.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeAndMark();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, guide]);

  function closeAndMark() {
    if (!guide) return;
    if (forcedKey) {
      // Forced opens are dismissed without affecting seen state.
      force(null);
      return;
    }
    markSeen(guideKey(guide));
  }

  function dismissAll() {
    if (guide) markSeen(guideKey(guide));
    muteAll();
  }

  return (
    <AnimatePresence>
      {open && guide ? (
        <motion.div
          key="first-visit-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 bg-overlay/75 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={closeAndMark}
          data-testid="first-visit-overlay"
        >
          <motion.div
            initial={{ y: 16, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 8, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="panel max-w-lg w-full p-0 overflow-hidden shadow-gold-glow"
            onClick={(e) => e.stopPropagation()}
            data-testid="first-visit-modal"
          >
            <header className="px-6 pt-5 pb-3 border-b border-gold-dim/15 flex items-start justify-between gap-4">
              <div>
                <p className="font-inter text-[10px] uppercase tracking-widest text-gold-bright flex items-center gap-1.5">
                  <BookOpen size={11} /> First visit
                </p>
                <h2 className="heading-display text-2xl text-cream mt-1 leading-tight">
                  {guide.title}
                </h2>
              </div>
              <button
                type="button"
                onClick={closeAndMark}
                className="btn-quiet p-1 shrink-0"
                aria-label="Close"
                data-testid="first-visit-close"
              >
                <X size={14} />
              </button>
            </header>

            <div className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
              <p className="font-serif text-cream-soft leading-relaxed">
                {guide.narration}
              </p>

              {guide.quote ? (
                <figure
                  className="rounded-md border border-gold-bright/30 bg-gold/5 px-4 py-3"
                  data-testid="first-visit-quote"
                >
                  <QuoteIcon size={12} className="text-gold-bright mb-1" />
                  <blockquote className="font-serif text-sm text-cream leading-relaxed">
                    &ldquo;{guide.quote.text}&rdquo;
                  </blockquote>
                  <figcaption className="font-inter text-[10px] uppercase tracking-widest text-gold-bright mt-2">
                    — {guide.quote.speaker}
                  </figcaption>
                </figure>
              ) : null}

              {guide.replaces ? (
                <div data-testid="first-visit-replaces">
                  <p className="font-inter text-[9px] uppercase tracking-widest text-cream-faint">
                    Replaces
                  </p>
                  <p className="font-serif text-cream-soft text-sm mt-1 leading-relaxed">
                    {guide.replaces}
                  </p>
                </div>
              ) : null}

              {guide.tryThis ? (
                <div
                  className="rounded-md border border-mint/40 bg-mint/5 px-3 py-2 flex gap-2"
                  data-testid="first-visit-try-this"
                >
                  <Sparkles size={12} className="text-mint shrink-0 mt-0.5" />
                  <p className="font-serif text-cream-soft text-sm leading-relaxed">
                    {guide.tryThis}
                  </p>
                </div>
              ) : null}
            </div>

            <footer className="px-6 py-3 border-t border-gold-dim/15 flex items-center justify-between gap-2 flex-wrap">
              <button
                type="button"
                onClick={dismissAll}
                className="btn-quiet text-[11px]"
                data-testid="first-visit-mute-all"
                title="Hide all first-visit modals (re-enable from the Help button)"
              >
                Don&apos;t show these again
              </button>
              <button
                type="button"
                onClick={closeAndMark}
                className="btn-primary text-xs"
                data-testid="first-visit-got-it"
              >
                Got it
              </button>
            </footer>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
